<template>
  <div>
    <v-form
      id="form"
      ref="form"
      class="m-4 mx-auto my-6 grid max-w-3xl grid-cols-2 rounded-lg bg-white p-4 md:gap-x-11"
      @submit.prevent="submit"
    >
      <button
        v-if="!user"
        class="text-vgorange col-span-full mb-4 text-left text-lg"
        @click="signIn()"
      >
        Already have an account?
      </button>

      <label class="col-span-2 text-sm md:col-span-1">
        Full Name
        <fa-icon class="text-vgorange" icon="fa-regular fa-asterisk" />
        <v-text-field
          v-model="consult.full_name"
          data-cy="consult.form.input.full-name"
          :rules="requiredRules"
          required
          :color="vgTeal"
          variant="outlined"
        ></v-text-field>
      </label>
      <label class="col-span-2 text-sm md:col-span-1">
        Preferred Name
        <fa-icon class="text-vgorange" icon="fa-regular fa-asterisk" />
        <v-text-field
          v-model="consult.known_as"
          data-cy="consult.form.input.known-as"
          :rules="requiredRules"
          required
          :color="vgTeal"
          variant="outlined"
        ></v-text-field>
      </label>
      <label class="col-span-2 text-sm md:col-span-1">
        Email
        <fa-icon class="text-vgorange" icon="fa-regular fa-asterisk" />
        <v-text-field
          v-model.trim="consult.email"
          data-cy="consult.form.input.email"
          :rules="emailRules"
          variant="outlined"
          required
          :color="vgTeal"
          @blur="checkEmailExist()"
        ></v-text-field>
      </label>
      <label class="col-span-2 text-sm md:col-span-1">
        Phone
        <fa-icon class="text-vgorange" icon="fa-regular fa-asterisk" />
        <v-text-field
          v-model="consult.phone_number"
          data-cy="consult.form.input.number"
          variant="outlined"
          :rules="phoneRules"
          required
          :color="vgTeal"
        ></v-text-field>
      </label>
      <label class="col-span-2 text-sm md:col-span-1">
        Website (optional)
        <v-text-field
          v-model="consult.website"
          data-cy="consult.form.input.website"
          :rules="websiteRules"
          variant="outlined"
          :color="vgTeal"
        ></v-text-field>
      </label>
      <label class="col-span-2 text-sm md:col-span-1">
        Industry
        <fa-icon class="text-vgorange" icon="fa-regular fa-asterisk" />
        <v-select
          v-model="consult.industry"
          data-cy="consult.form.dropdown.industry"
          :items="clientIndustries"
          variant="outlined"
          :rules="requiredRules"
          required
          :color="vgTeal"
        ></v-select>
      </label>
      <div class="col-span-2 flex flex-row p-0 pb-2">
        <v-icon
          v-if="consult.is_individual"
          data-cy="is_individual"
          size="20"
          :color="vgNavy"
          @click="consult.is_individual = !consult.is_individual"
        >
          mdi-checkbox-marked-outline
        </v-icon>
        <v-icon
          v-else
          size="20"
          :color="vgNavy"
          @click="consult.is_individual = !consult.is_individual"
        >
          mdi-checkbox-blank-outline
        </v-icon>
        <p class="text-vgnavy -mb-0.5 pl-1 text-base">
          I don't represent a business.
        </p>
      </div>
      <v-expand-transition>
        <label
          v-show="!consult.is_individual"
          class="col-span-2 text-sm md:col-span-1"
        >
          Business Name
          <fa-icon class="text-vgorange" icon="fa-regular fa-asterisk" />
          <v-text-field
            v-model="consult.company"
            data-cy="consult.form.input.company"
            variant="outlined"
            :rules="businessRequiredRules"
            :color="vgTeal"
          ></v-text-field>
        </label>
      </v-expand-transition>
      <v-expand-transition>
        <label
          v-if="!consult.is_individual"
          class="col-span-2 text-sm md:col-span-1"
        >
          Role/Title
          <fa-icon class="text-vgorange" icon="fa-regular fa-asterisk" />
          <v-text-field
            v-model="consult.title"
            data-cy="consult.form.input.title"
            variant="outlined"
            :rules="requiredRules"
            :color="vgTeal"
          ></v-text-field>
        </label>
      </v-expand-transition>

      <div
        v-if="googleReady && !addressInputDisable"
        class="col-span-2 md:col-span-1"
      >
        <label class="text-sm">
          Location (city)
          <fa-icon class="text-vgorange" icon="fa-regular fa-asterisk" />

          <vue-google-autocomplete
            id="google-address-input"
            ref="addressInput"
            :class="{ addressError: showAddressInputError }"
            data-cy="consult.form.input.city"
            autocomplete="off"
            classname="city-autocomplete"
            placeholder=""
            types="(cities)"
            @blur="addressInputHasFocused = true"
            @placechanged="cityPicked"
            @no-results-found="googleLocationNotFound = true"
            @keypress.enter.prevent
          >
          </vue-google-autocomplete>
          <p
            class="pl-2 pt-1 text-xs text-[rgb(176,0,32)]"
            :style="{
              visibility: showAddressInputError ? 'visible' : 'hidden',
            }"
          >
            Select the city from the generated list once you start typing
          </p>
        </label>
      </div>
      <label
        v-if="addressInputDisable"
        class="col-span-2 text-sm md:col-span-1"
      >
        Location (city)
        <fa-icon class="text-vgorange" icon="fa-regular fa-asterisk" />
        <v-text-field
          :model-value="getAddress"
          :color="vgTeal"
          readonly
          variant="outlined"
          hint="Click on pen to edit location"
        >
          <template #append-inner>
            <fa-icon
              icon="fa-regular fa-pencil"
              class="text-vgstone -mt-1 pl-2 text-xl"
              @click="clearLocationData"
            />
          </template>
        </v-text-field>
      </label>
      <v-expand-transition>
        <label
          v-show="!consult.is_individual"
          class="col-span-2 text-sm md:col-span-1"
        >
          Business Size
          <fa-icon class="text-vgorange" icon="fa-regular fa-asterisk" />
          <v-select
            v-model="consult.size"
            data-cy="consult.form.dropdown.size"
            :items="clientSizes"
            variant="outlined"
            :rules="businessRequiredRules"
            required
            :color="vgTeal"
          ></v-select>
        </label>
      </v-expand-transition>
    </v-form>
    <v-dialog v-model="emailExist" persistent width="600">
      <div class="bg-vgsilver-100 rounded-md">
        <div class="p-8">
          <p class="text-lg font-semibold">
            We've detected that you already have an account. Please sign in.
          </p>
          <div
            class="flex flex-col-reverse justify-center gap-2 pt-5 md:flex-row"
          >
            <vg-btn vg-size="large" @click="signIn()">Sign in</vg-btn>
          </div>
        </div>
      </div>
    </v-dialog>
  </div>
</template>
<script>
import { mapStores, storeToRefs } from 'pinia';
import { useConsultStore } from '@/stores/consult';
import { useUserStore } from '@/stores/user';
import tailwind from 'tailwind.config';
import {
  isValidEmail,
  isValidPhone,
  isValidDomainName,
} from '@/services/validationService';
import VueGoogleAutocomplete from 'vue-google-autocomplete';
import gmapsInit from '@/utils/gmaps';
import { getUserByEmail } from '@/services/userService';

export default {
  name: 'ConsultForm',
  components: { VueGoogleAutocomplete },
  emits: ['submit'],
  data() {
    const { user, client, clientConfig } = storeToRefs(useUserStore());

    return {
      user,
      client,
      clientConfig,
      consult: {
        full_name: '',
        known_as: '',
        email: '',
        phone_number: '',
        website: '',
        industry: '',
        is_individual: false,
        company: '',
        size: 'micro',
        title: '',
        city: '',
      },
      requiredRules: [(v) => !!v || 'Required'],
      emailRules: [
        (v) => !!v || 'Required',
        (v) => isValidEmail(v) || 'E-mail must be valid',
      ],
      phoneRules: [
        (v) => !!v || 'Required',
        (v) => isValidPhone(v) || 'Phone must be valid',
      ],
      businessRequiredRules: [
        (v) => this.consult.is_individual || !!this.user || !!v || 'Required',
      ],
      websiteRules: [
        (v) =>
          v === '' || isValidDomainName(v) || 'Website domain must be valid',
      ],
      vgTeal: tailwind.theme.extend.colors.vgteal[500],
      addressInputHasFocused: false,
      googleReady: false,
      vgNavy: tailwind.theme.extend.colors.vgnavy[500],
      addressInputDisable: false,
      googleLocationNotFound: false,
      emailExist: false,
    };
  },
  computed: {
    ...mapStores(useConsultStore),
    clientIndustries: function () {
      if (!this.clientConfig) {
        return [];
      }
      return this.clientConfig.client_industries.map((industry) => {
        return { value: industry.id, title: industry.value };
      });
    },
    clientSizes: function () {
      if (!this.clientConfig) {
        return [];
      }
      return this.clientConfig.clientSizes;
    },
    showAddressInputError: function () {
      return (
        (!this.consult.country &&
          !this.consult.state_prov &&
          !this.consult.city &&
          this.addressInputHasFocused) ||
        this.googleLocationNotFound
      );
    },
    getAddress() {
      return `${this.consult.city}, ${this.consult.state_prov}, ${this.consult.country}`;
    },
  },
  async created() {
    await gmapsInit();
    this.googleReady = true;

    if (this.user) {
      this.consult.company = this.user.company;
      this.consult.full_name = this.user.full_name;
      this.consult.known_as = this.user.known_as;
      this.consult.email = this.user.email;
      this.consult.phone_number = this.user.phone_number;
    }
    if (this.client) {
      this.consult.website = this.client.client.website;
      this.consult.industry = this.client.client.industry;
      this.consult.size = this.client.client.size;
    }
  },
  methods: {
    cityPicked(location, place) {
      for (const component of place.address_components) {
        const componentType = component.types[0];

        switch (componentType) {
          case 'administrative_area_level_1': {
            this.consult.state_prov = component.long_name;
            break;
          }
          case 'country': {
            this.consult.country = component.long_name;
            break;
          }
          case 'locality': {
            this.consult.city = component.long_name;
            break;
          }
        }
      }

      this.consult.latitude = location.latitude;
      this.consult.longitude = location.longitude;
      this.googleLocationNotFound = false;
      this.addressInputDisable = true;
    },
    clearLocationData() {
      this.consult.country = '';
      this.consult.state_prov = '';
      this.consult.city = '';
      this.consult.latitude = '';
      this.consult.longitude = '';
      this.addressInputDisable = false;
    },
    async submit() {
      if (!(await this.$refs.form.validate()).valid) {
        return;
      }

      if (!this.user && !this.consult.city) {
        return this.$root.$snackbar.error('Please specify your location');
      }
      this.$emit('submit', this.consult);
    },
    signIn() {
      this.emailExist = false;
      this.consultStore.updateConsult({
        ...this.consultStore.consultState,
        stage: 'signIn',
      });
      this.$router.push('/');
    },

    async checkEmailExist() {
      if (this.consult.email) {
        const email = await getUserByEmail(this.consult.email).catch(
          () => false
        );

        if (
          email &&
          email.permissions
            ?.split(',')
            .find((permission) => permission === 'client')
        ) {
          this.emailExist = true;
        }
      }
    },
  },
};
</script>
